// Engineer-only score-debug badge: per-tile hover card showing the OpenSearch score breakdown.
import type {ReactElement} from 'react';
import React, {useState} from 'react';
import {createPortal} from 'react-dom';
import {Icon} from '@Components/icon-v2';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {ClickableDiv} from '@Components/clickable-div';
import {assertNever} from '@Utils/assert-never.util';
import type {StockItemScoreDebug, StockSearchScoreDebug} from '@Panels/stock-panel/stock-panel.types';
import {getStockItemScoreDebug, isStockScoreDebugActive} from '@Panels/stock-panel/stock-score-debug';
import type {CardPosition, StockScoreDebugBadgeProps, StockScoreDebugRow} from './stock-score-debug-badge.types';
import {STOCK_SCORE_DEBUG_CARD_OFFSET, STOCK_SCORE_DEBUG_CARD_WIDTH, STOCK_SCORE_DEBUG_DESCRIPTION_MAX} from './stock-score-debug-badge.types';
import styles from './stock-score-debug-badge.module.scss';

const formatScore = (value: number | null): string => {
  return value === null ? '—' : value.toFixed(4);
};

const formatCutoff = (value: number | string): string => {
  return typeof value === 'string' ? value : value.toFixed(4);
};

const truncate = (value: string): string => {
  return value.length > STOCK_SCORE_DEBUG_DESCRIPTION_MAX ? `${value.slice(0, STOCK_SCORE_DEBUG_DESCRIPTION_MAX)}…` : value;
};

const scoreRow = (label: string, value: string): StockScoreDebugRow => {
  return {kind: 'row', label, value};
};

const boolRow = (label: string, value: boolean): StockScoreDebugRow => {
  return {kind: 'row', label, value: value ? 'yes' : 'no', valueClass: value ? styles.good : styles.bad};
};

const buildSearchRows = (search: StockSearchScoreDebug | null): StockScoreDebugRow[] => {
  if (search === null) {
    return [scoreRow('cutoff', 'n/a')];
  }
  return [
    scoreRow('top score', formatScore(search.topScore)),
    scoreRow('ratio floor', search.ratioFloor === null ? 'n/a' : formatScore(search.ratioFloor)),
    scoreRow('cutoff', formatCutoff(search.cutoff)),
    scoreRow('winner', search.winner),
  ];
};

const buildCardRows = (item: StockItemScoreDebug): StockScoreDebugRow[] => {
  return [
    scoreRow('type', item.type ?? '—'),
    scoreRow('tokens', String(item.tokenCount)),
    scoreRow('query path', item.queryPath),
    scoreRow('keywords', item.keywords ? item.keywords.join(', ') : '—'),
    scoreRow('description', item.description ? truncate(item.description) : '—'),
    {kind: 'divider'},
    scoreRow('final score', formatScore(item.finalScore)),
    scoreRow('neural score', formatScore(item.neuralScore)),
    scoreRow('neural cosine', formatScore(item.neuralCosine)),
    scoreRow('neural floor', formatScore(item.neuralFloor)),
    boolRow('cleared floor', item.clearedNeuralFloor),
    scoreRow('lexical score', formatScore(item.lexicalScore)),
    boolRow('matched lexical', item.matchedLexical),
    {kind: 'divider'},
    {kind: 'title', text: 'search cutoff'},
    ...buildSearchRows(item.search),
  ];
};

const renderCardRow = (row: StockScoreDebugRow, index: number): ReactElement => {
  const key = `${row.kind}-${index}`;
  switch (row.kind) {
    case 'divider':
      return <div key={key} className={styles.divider} />;
    case 'title':
      return <Text key={key} className={styles.sectionTitle} val={row.text} size={TextSize.XS} bold />;
    case 'row':
      return (
        <div key={key} className={styles.row}>
          <Text className={styles.label} val={row.label} size={TextSize.XS} bold />
          <Text className={`${styles.value} ${row.valueClass ?? ''}`} val={row.value} size={TextSize.XS} />
        </div>
      );
    default:
      return assertNever(row);
  }
};

export function StockScoreDebugBadge({idStockMedia}: StockScoreDebugBadgeProps): ReactElement | null {
  const [position, setPosition] = useState<CardPosition | null>(null);

  if (!isStockScoreDebugActive()) {
    return null;
  }

  const debug = getStockItemScoreDebug(idStockMedia);
  if (!debug) {
    return null;
  }

  const onEnter = (e: React.MouseEvent<HTMLDivElement>): void => {
    const rect = e.currentTarget.getBoundingClientRect();
    setPosition({
      top: rect.bottom + STOCK_SCORE_DEBUG_CARD_OFFSET,
      left: Math.max(STOCK_SCORE_DEBUG_CARD_OFFSET, rect.right - STOCK_SCORE_DEBUG_CARD_WIDTH),
    });
  };

  const onLeave = (): void => {
    setPosition(null);
  };

  const renderCard = (item: StockItemScoreDebug): ReactElement | null => {
    if (position === null) {
      return null;
    }
    return createPortal(
      <div className={styles.card} style={{top: position.top, left: position.left, width: STOCK_SCORE_DEBUG_CARD_WIDTH}}>
        {buildCardRows(item).map(renderCardRow)}
      </div>,
      document.body
    );
  };

  const swallow = (e: React.SyntheticEvent): void => {
    e.stopPropagation();
  };

  return (
    <ClickableDiv className={styles.badge} onClick={swallow} onPointerDown={swallow} onMouseEnter={onEnter} onMouseLeave={onLeave} enableFocusOutline={false}>
      <Icon icon="icon-info" size={IconSize.SIZE_ICON_16} />
      {renderCard(debug)}
    </ClickableDiv>
  );
}
